import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Loader2, Receipt, Download, ChevronLeft, ChevronRight, Info, FileText, ListOrdered
} from 'lucide-react';
import { useApi } from '../utils/useApi';
import { useUserContext } from '../contexts/UserContext';
import { useBranch } from '../contexts/BranchContext';

interface RegisterRow {
  invoiceId: string | null;
  invoiceDate: string;
  memberName: string;
  source: 'purchase' | 'membership';
  documentType: string;
  status: string;
  cancelled: boolean;
  branchName: string | null;
  gstin: string | null;
  recipientGstin: string | null;
  recipientLegalName: string | null;
  ratePercent: number;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  totalTax: number;
  invoiceTotal: number;
  refundAmount: number;
}

interface RateRow { ratePercent: number; taxableValue: number; cgst: number; sgst: number; igst: number; totalTax: number }
interface CustomerSplitPart { taxableValue: number; cgst: number; sgst: number; igst: number; totalTax: number; invoiceCount: number }
interface RegistrationBlock {
  gstin: string | null;
  documentType: string;
  summary: { taxableValue: number; cgst: number; sgst: number; igst: number; totalTax: number; invoiceCount: number; refundTotal: number };
  rateWise: RateRow[];
  customerSplit?: { b2b: CustomerSplitPart; b2c: CustomerSplitPart };
}
interface ReportData {
  from: string;
  to: string;
  registrations: RegistrationBlock[];
  grandTotal: {
    taxableValue: number; cgst: number; sgst: number; igst: number; totalTax: number;
    invoiceCount: number; cancelledCount: number; refundTotal: number;
    b2bInvoiceCount?: number; b2cInvoiceCount?: number;
  };
  register: RegisterRow[];
}

const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 });

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

// Indian FY quarter containing the month: Apr–Jun, Jul–Sep, Oct–Dec, Jan–Mar
function quarterRange(month: string): { from: string; to: string; label: string } {
  const [y, m] = month.split('-').map(Number);
  const qStartMonth = m <= 3 ? 1 : m <= 6 ? 4 : m <= 9 ? 7 : 10;
  const fyQ = qStartMonth === 4 ? 'Q1' : qStartMonth === 7 ? 'Q2' : qStartMonth === 10 ? 'Q3' : 'Q4';
  const from = `${y}-${String(qStartMonth).padStart(2, '0')}`;
  const to = `${y}-${String(qStartMonth + 2).padStart(2, '0')}`;
  return { from, to, label: `${fyQ} · ${monthLabel(from)} – ${monthLabel(to)}` };
}

function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const escape = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const blockKey = (b: RegistrationBlock) => b.gstin || '__bos__';
const isBosBlock = (b: RegistrationBlock) => b.documentType === 'bill_of_supply' || !b.gstin;
const emptyRate = (ratePercent: number): RateRow => ({ ratePercent, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0 });

export default function GstReportPage() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();
  const { selectedBranchId } = useBranch();

  const [month, setMonth] = useState(currentMonth());
  const [period, setPeriod] = useState<'month' | 'quarter'>('month');
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'summary' | 'register'>('summary');
  const [gstinFilter, setGstinFilter] = useState<string>('all'); // 'all' | blockKey

  const ready = !!userContext && !userLoading;
  const range = period === 'month' ? { from: month, to: month } : quarterRange(month);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const branchParam = selectedBranchId ? `&branchId=${selectedBranchId}` : '';
      const res = await fetchWithAuth(`/api/gst-reports?from=${range.from}&to=${range.to}${branchParam}`);
      setData(res.data || null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load GST report');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, range.from, range.to, selectedBranchId]);

  useEffect(() => {
    if (!ready) return;
    load();
  }, [ready, load]);

  const periodSlug = period === 'month' ? month : `${range.from}_to_${range.to}`;

  const exportRegister = (rows: RegisterRow[], tag: string) => {
    downloadCsv(
      `gst-sales-register-${tag}-${periodSlug}.csv`,
      ['Invoice No', 'Invoice Date', 'Customer', 'Document', 'Supply', 'Buyer GSTIN', 'Buyer Name', 'GSTIN', 'Branch', 'Status', 'GST Rate %', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Tax', 'Invoice Total', 'Refund'],
      rows.map(r => [
        r.invoiceId || '', r.invoiceDate, r.memberName,
        r.documentType === 'bill_of_supply' ? 'Bill of Supply' : 'Tax Invoice',
        r.recipientGstin ? 'B2B' : 'B2C', r.recipientGstin || '', r.recipientLegalName || '',
        r.gstin || '', r.branchName || '',
        r.cancelled ? 'CANCELLED' : r.status, r.ratePercent, r.taxableValue.toFixed(2),
        r.cgst.toFixed(2), r.sgst.toFixed(2), r.igst.toFixed(2), r.totalTax.toFixed(2), r.invoiceTotal.toFixed(2), r.refundAmount.toFixed(2),
      ])
    );
  };

  const exportRateWise = (rows: RateRow[], tag: string) => {
    downloadCsv(
      `gst-rate-summary-${tag}-${periodSlug}.csv`,
      ['GST Rate %', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Tax'],
      rows.map(r => [r.ratePercent, r.taxableValue.toFixed(2), r.cgst.toFixed(2), r.sgst.toFixed(2), r.igst.toFixed(2), r.totalTax.toFixed(2)])
    );
  };

  const g = data?.grandTotal;
  const blocks = data?.registrations ?? [];

  // GSTIN is a filter on the active table, not a tab. 'all' aggregates every
  // registration; a stale selection (after a period change) falls back to 'all'.
  const filterValid = gstinFilter === 'all' || blocks.some(b => blockKey(b) === gstinFilter);
  const effFilter = filterValid ? gstinFilter : 'all';
  const selectedBlock = effFilter === 'all' ? null : blocks.find(b => blockKey(b) === effFilter) ?? null;
  const selIsBos = !!selectedBlock && isBosBlock(selectedBlock);
  const filterTag = selectedBlock ? (selectedBlock.gstin || 'bill-of-supply') : 'all-gstins';

  const filteredRegister = data
    ? (effFilter === 'all'
        ? data.register
        : data.register.filter(r =>
            selIsBos ? (r.documentType === 'bill_of_supply' || !r.gstin) : r.gstin === selectedBlock!.gstin))
    : [];
  const hasIgst = filteredRegister.some(r => r.igst > 0);

  // Rate rows for the active filter: a single block's, or all blocks merged by rate.
  const rateRows: RateRow[] = selectedBlock
    ? selectedBlock.rateWise
    : (() => {
        const m = new Map<number, RateRow>();
        for (const b of blocks) for (const r of b.rateWise) {
          const c = m.get(r.ratePercent) ?? emptyRate(r.ratePercent);
          c.taxableValue += r.taxableValue; c.cgst += r.cgst; c.sgst += r.sgst; c.igst += r.igst; c.totalTax += r.totalTax;
          m.set(r.ratePercent, c);
        }
        return [...m.values()].sort((a, b) => a.ratePercent - b.ratePercent);
      })();
  const rateTotal = rateRows.reduce((a, r) => {
    a.taxableValue += r.taxableValue; a.cgst += r.cgst; a.sgst += r.sgst; a.igst += r.igst; a.totalTax += r.totalTax;
    return a;
  }, emptyRate(0));

  // Summary cards follow the filter: a block's own totals, else the grand total.
  const sum = selectedBlock
    ? selectedBlock.summary
    : (g ? { taxableValue: g.taxableValue, cgst: g.cgst, sgst: g.sgst, igst: g.igst, totalTax: g.totalTax, invoiceCount: g.invoiceCount, refundTotal: g.refundTotal } : undefined);

  const filterContext = selectedBlock
    ? (selIsBos ? 'Bill of Supply' : `GSTIN ${selectedBlock.gstin}`)
    : 'All GSTINs (combined)';

  // B2B (buyer GSTIN present) vs B2C split for the active filter: a single
  // block's own split, or every block merged. Drives the customer-split strip
  // and only appears once there's at least one B2B invoice in view.
  const emptyPart = (): CustomerSplitPart => ({ taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0, invoiceCount: 0 });
  const custSplit = selectedBlock
    ? selectedBlock.customerSplit
    : (() => {
        const acc = { b2b: emptyPart(), b2c: emptyPart() };
        for (const b of blocks) {
          if (!b.customerSplit) continue;
          for (const k of ['b2b', 'b2c'] as const) {
            acc[k].taxableValue += b.customerSplit[k].taxableValue;
            acc[k].totalTax += b.customerSplit[k].totalTax;
            acc[k].invoiceCount += b.customerSplit[k].invoiceCount;
          }
        }
        return acc;
      })();

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-teal-500" /> GST Report
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Filable per GSTIN. Download and share with your CA.
          </p>
        </div>
        <button
          onClick={() => data && exportRegister(data.register, 'all')} disabled={!data || data.register.length === 0}
          className="px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 flex items-center gap-1.5 disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> Full Register CSV
        </button>
      </div>

      {/* Period controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center rounded-xl border border-gray-300 bg-white shadow-sm overflow-hidden">
          <button
            onClick={() => setMonth(m => shiftMonth(m, period === 'month' ? -1 : -3))}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 border-r border-gray-200 transition-colors"
            aria-label="Previous period"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-4 py-2 text-sm font-bold text-gray-900 min-w-[150px] text-center select-none">
            {period === 'month' ? monthLabel(month) : quarterRange(month).label}
          </span>
          <button
            onClick={() => setMonth(m => shiftMonth(m, period === 'month' ? 1 : 3))}
            disabled={month >= currentMonth()}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 border-l border-gray-200 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-600"
            aria-label="Next period"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex rounded-xl border border-gray-300 overflow-hidden shadow-sm">
          {(['month', 'quarter'] as const).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                period === p ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-400 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Invoice-basis (tax on invoices raised in the period, not payments collected). Each GSTIN is
        filable on its own return; Bill-of-Supply (unregistered) sales carry no tax. Cancelled invoices are
        listed but excluded from totals.
      </p>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
      ) : !data || blocks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-sm text-gray-400">
          No invoices in this period.
        </div>
      ) : (
        <>
          {/* Tabs (one per table) + GSTIN filter */}
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200">
            <div className="flex gap-1">
              {([
                { key: 'summary', label: 'Rate Summary', icon: FileText },
                { key: 'register', label: 'Invoice Register', icon: ListOrdered },
              ] as const).map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                    tab === t.key
                      ? 'border-teal-600 text-teal-700'
                      : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                  }`}
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pb-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">GSTIN</span>
              <select
                value={effFilter}
                onChange={e => setGstinFilter(e.target.value)}
                className="text-sm font-semibold text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              >
                <option value="all">All GSTINs</option>
                {blocks.map(b => (
                  <option key={blockKey(b)} value={blockKey(b)}>
                    {isBosBlock(b) ? 'Bill of Supply' : b.gstin}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary cards — scoped to the GSTIN filter */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {(selIsBos
              ? [
                  { label: 'Taxable Value', value: sum ? inr.format(sum.taxableValue) : '—' },
                  { label: 'Invoices', value: sum ? String(sum.invoiceCount) : '—' },
                ]
              : [
                  { label: 'Taxable Value', value: sum ? inr.format(sum.taxableValue) : '—' },
                  { label: hasIgst ? 'IGST' : 'CGST', value: sum ? inr.format(hasIgst ? sum.igst : sum.cgst) : '—' },
                  { label: hasIgst ? 'CGST + SGST' : 'SGST', value: sum ? inr.format(hasIgst ? sum.cgst + sum.sgst : sum.sgst) : '—' },
                  { label: 'Total Tax', value: sum ? inr.format(sum.totalTax) : '—' },
                ]
            ).map(card => (
              <div key={card.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{card.label}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{card.value}</p>
              </div>
            ))}
          </div>

          {/* B2B vs B2C split — shown only when there are B2B (business-buyer) sales */}
          {custSplit && custSplit.b2b.invoiceCount > 0 && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 bg-teal-50/60 border border-teal-100 rounded-xl px-4 py-2.5 text-sm">
              <span className="text-[11px] font-bold text-teal-700 uppercase tracking-wide">Customer Split</span>
              <span className="text-gray-600">
                <span className="font-bold text-gray-900">B2B</span> · {custSplit.b2b.invoiceCount} inv · {inr.format(custSplit.b2b.taxableValue)} taxable · {inr.format(custSplit.b2b.totalTax)} tax
              </span>
              <span className="text-gray-600">
                <span className="font-bold text-gray-900">B2C</span> · {custSplit.b2c.invoiceCount} inv · {inr.format(custSplit.b2c.taxableValue)} taxable · {inr.format(custSplit.b2c.totalTax)} tax
              </span>
            </div>
          )}

          {/* Active table */}
          {tab === 'summary' ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <FileText className={`w-4 h-4 ${selIsBos ? 'text-gray-400' : 'text-teal-500'}`} />
                  <span className="text-sm font-bold text-gray-800">Rate-wise Summary</span>
                  <span className="text-[11px] text-gray-400">· {filterContext}</span>
                </div>
                {!selIsBos && (
                  <button
                    onClick={() => exportRateWise(rateRows, filterTag)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" /> Rate Summary CSV
                  </button>
                )}
              </div>

              {selIsBos ? (
                <p className="text-sm text-gray-500">
                  Unregistered sales — {sum ? inr.format(sum.taxableValue) : '—'} across{' '}
                  {sum?.invoiceCount ?? 0} invoice{sum?.invoiceCount === 1 ? '' : 's'}, no tax collected.
                </p>
              ) : rateRows.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">No taxable invoices for this filter.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                        <th className="py-2 pr-4 font-semibold">Rate</th>
                        <th className="py-2 pr-4 font-semibold text-right">Taxable</th>
                        <th className="py-2 pr-4 font-semibold text-right">CGST</th>
                        <th className="py-2 pr-4 font-semibold text-right">SGST</th>
                        <th className="py-2 pr-4 font-semibold text-right">IGST</th>
                        <th className="py-2 font-semibold text-right">Total Tax</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {rateRows.map(r => (
                        <tr key={r.ratePercent}>
                          <td className="py-2.5 pr-4 font-semibold text-gray-800">{r.ratePercent}%</td>
                          <td className="py-2.5 pr-4 text-right text-gray-700">{inr.format(r.taxableValue)}</td>
                          <td className="py-2.5 pr-4 text-right text-gray-700">{inr.format(r.cgst)}</td>
                          <td className="py-2.5 pr-4 text-right text-gray-700">{inr.format(r.sgst)}</td>
                          <td className="py-2.5 pr-4 text-right text-gray-700">{inr.format(r.igst)}</td>
                          <td className="py-2.5 text-right font-semibold text-gray-900">{inr.format(r.totalTax)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-200">
                        <td className="py-2.5 pr-4 font-bold text-gray-800">Total</td>
                        <td className="py-2.5 pr-4 text-right font-bold text-gray-900">{inr.format(rateTotal.taxableValue)}</td>
                        <td className="py-2.5 pr-4 text-right font-bold text-gray-900">{inr.format(rateTotal.cgst)}</td>
                        <td className="py-2.5 pr-4 text-right font-bold text-gray-900">{inr.format(rateTotal.sgst)}</td>
                        <td className="py-2.5 pr-4 text-right font-bold text-gray-900">{inr.format(rateTotal.igst)}</td>
                        <td className="py-2.5 text-right font-bold text-gray-900">{inr.format(rateTotal.totalTax)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {sum && sum.refundTotal > 0 && (
                <p className="text-xs text-gray-400 mt-3">
                  Refunds in period: {inr.format(sum.refundTotal)} — ask your CA about credit notes.
                </p>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <ListOrdered className="w-4 h-4 text-teal-500" />
                  <span className="text-sm font-bold text-gray-800">Invoice Register</span>
                  <span className="text-[11px] text-gray-400">
                    · {filterContext}{filteredRegister.length > 0 && ` · ${filteredRegister.length} rows`}
                  </span>
                </div>
                <button
                  onClick={() => exportRegister(filteredRegister, filterTag)}
                  disabled={filteredRegister.length === 0}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-40"
                >
                  <Download className="w-3.5 h-3.5" /> Register CSV
                </button>
              </div>
              {filteredRegister.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">No invoices for this filter in the period.</p>
              ) : (
                <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                        <th className="py-2 pr-4 font-semibold">Invoice</th>
                        <th className="py-2 pr-4 font-semibold">Date</th>
                        <th className="py-2 pr-4 font-semibold">Customer</th>
                        <th className="py-2 pr-4 font-semibold text-right">Rate</th>
                        <th className="py-2 pr-4 font-semibold text-right">Taxable</th>
                        <th className="py-2 pr-4 font-semibold text-right">CGST</th>
                        <th className="py-2 pr-4 font-semibold text-right">SGST</th>
                        {hasIgst && <th className="py-2 pr-4 font-semibold text-right">IGST</th>}
                        <th className="py-2 font-semibold text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredRegister.map((r, i) => (
                        <tr key={`${r.invoiceId}-${r.ratePercent}-${i}`} className={r.cancelled ? 'opacity-50' : ''}>
                          <td className={`py-2 pr-4 font-medium text-gray-800 whitespace-nowrap ${r.cancelled ? 'line-through' : ''}`}>
                            {r.invoiceId || '—'}
                            {r.documentType === 'bill_of_supply' && <span className="ml-1.5 text-[10px] font-bold text-gray-400 uppercase no-underline">BoS</span>}
                            {r.cancelled && <span className="ml-1.5 text-[10px] font-bold text-red-400 uppercase no-underline">cancelled</span>}
                          </td>
                          <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">
                            {new Date(r.invoiceDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </td>
                          <td className="py-2 pr-4 text-gray-700 max-w-[180px]">
                            <div className="truncate">
                              {r.memberName}
                              {r.branchName && <span className="ml-1.5 text-[10px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{r.branchName}</span>}
                            </div>
                            {r.recipientGstin && (
                              <div className="text-[10px] font-semibold text-teal-600 truncate" title={r.recipientLegalName || undefined}>B2B · {r.recipientGstin}</div>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-right text-gray-500">{r.ratePercent}%</td>
                          <td className="py-2 pr-4 text-right text-gray-700">{inr.format(r.taxableValue)}</td>
                          <td className="py-2 pr-4 text-right text-gray-500">{inr.format(r.cgst)}</td>
                          <td className="py-2 pr-4 text-right text-gray-500">{inr.format(r.sgst)}</td>
                          {hasIgst && <td className="py-2 pr-4 text-right text-gray-500">{inr.format(r.igst)}</td>}
                          <td className="py-2 text-right font-semibold text-gray-900">{inr.format(r.invoiceTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
